// Form workflows — the workflow list behind the Forms-page "Workflow" modal via
// POST /workflows/all (server-side pagination). Request + response shapes are
// confirmed; normalization stays isolated in toWorkflow (same idiom as formsApi)
// so any backend drift is a one-file fix. The display status (published/draft)
// is derived — the list item carries no explicit flag (see toWorkflow).
//
// GET /workflows/:id/versions is the read-only sibling of /details behind the
// builder's Version history modal: same domain, flatter shape (no ids, role and
// routing target already resolved to names), so it needs ordering rather than
// mapping.
//
// PATCH /workflows/:id/status and POST /workflows/:id/duplicate back the list's
// row kebab. Neither response is modelled — nothing reads them, they only
// invalidate.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

/** Which list bucket to fetch — the modal's tab lifecycle. */
export type WorkflowLifecycle = 'active' | 'archived';

/** Display state shown in the Status column (derived from version keys).
 *  `both` = a published version plus a newer draft in progress. */
export enum WorkflowListingState {
  Both = 'both',
  Draft = 'draft',
  Published = 'published',
}

export interface WorkflowItem {
  id: string;
  name: string;
  /** Version label, e.g. "v3", or null when absent. */
  version: string | null;
  /** The same current version as a NUMBER, beside the display label — the
   *  version-history modal picks its "Current" row by number, and parsing it
   *  back out of the label would reverse a formatting step. */
  versionNumber: number | null;
  state: WorkflowListingState;
  /** ISO timestamp of the last update, or null. */
  updatedAt: string | null;
}

/** What PATCH /workflows/:id/status accepts — a superset of WorkflowLifecycle.
 *  'deleted' is a DESTINATION only, never a list filter: there is no Deleted
 *  tab and POST /workflows/all would not know what to do with it, which is why
 *  the move target and the list filter are no longer the same type. */
export type WorkflowStatusTarget = WorkflowLifecycle | 'deleted';

/** PATCH /workflows/:id/status — the archive / restore / delete lifecycle move. */
export interface WorkflowStatusPayload {
  id: string;
  status: WorkflowStatusTarget;
}

export interface WorkflowPage {
  items: WorkflowItem[];
  total: number;
}

/** POST /workflows/all body — `status` (tab lifecycle), `page`, `limit` are
 *  required; `search` is omitted (undefined) when empty. */
export interface WorkflowQueryArgs {
  status: WorkflowLifecycle;
  search?: string;
  page: number;
  limit: number;
}

/** One version of a workflow (Setup-tab version select). */
export interface WorkflowVersionOption {
  id: string;
  version: number;
}

/** GET /workflows/selection item — the Setup tab's Approval-workflow dropdown
 *  (versions sorted newest first). */
export interface WorkflowSelectionItem {
  id: string;
  title: string;
  versions: WorkflowVersionOption[];
}

/** Lifecycle of one workflow version (lowercase on the wire). */
export enum WorkflowVersionState {
  Draft = 'draft',
  Published = 'published',
}

/** Routing action for a step's response element. */
export enum WorkflowElementActionType {
  BackToUser = 'back_to_user',
  EndWorkflow = 'end_workflow',
  NextStep = 'next_step',
}

/** A response→routing row. Server-owned ids are null on creation and only
 *  populated in edit cases once the backend has returned them. */
export interface WorkflowElementPayload {
  _id: string | null;
  elementGlobalId: string | null;
  /** The response tag's title (the chosen response option). */
  title: string;
  position: number;
  actionType: WorkflowElementActionType;
  nextStepId: string | null;
  /** The target step's client `stepReferenceId` for `next_step`, else null. */
  nextStepReferenceId: string | null;
}

export interface WorkflowStepPayload {
  _id: string | null;
  stepGlobalId: string | null;
  title: string;
  position: number;
  /** Client-generated uuid linking this step (targets reference it). */
  stepReferenceId: string;
  /** Role id. */
  role: string;
  elements: WorkflowElementPayload[];
}

/** POST /workflows/draft and /workflows/publish body (same shape). */
export interface WorkflowSavePayload {
  workflowId: string | null;
  title: string;
  steps: WorkflowStepPayload[];
}

/** Save/publish response — the created/updated workflow's id. */
export interface WorkflowSaveResult {
  workflowId: string;
}

// ---- workflow detail (GET /workflows/:id/details) — seeds the edit builder ----
export interface WorkflowDetailElement {
  id: string;
  elementGlobalId: string | null;
  /** Response tag title. */
  title: string;
  position: number;
  actionType: WorkflowElementActionType;
  /** Target step id for `next_step`, else null. */
  nextStepId: string | null;
}
export interface WorkflowDetailStep {
  id: string;
  /** Always present on a saved step — it's the step's stable cross-version
   *  identity, and the builder uses it directly as the local `stepRefId`. */
  stepGlobalId: string;
  title: string;
  position: number;
  roleId: string;
  elements: WorkflowDetailElement[];
}
export interface WorkflowDetail {
  id: string;
  title: string;
  version: number | null;
  versionState: WorkflowVersionState | null;
  steps: WorkflowDetailStep[];
}

// ---- version history (GET /workflows/:id/versions) ----
// A flatter, read-only view of the same domain than the detail above: no ids
// anywhere (versions, steps and elements are all identified by position), the
// role arrives as its NAME rather than an id, and a routing target arrives as
// the step's TITLE rather than a stepGlobalId. Nothing here is editable, so
// none of those ids would have anywhere to go.
export interface WorkflowVersionElement {
  /** The response tag's title. */
  title: string;
  actionType: WorkflowElementActionType;
  /** The target step's TITLE for `next_step` — null when the backend has not
   *  resolved one, and always null for the two terminal actions. A null target
   *  is NOT the following step in the list: v1 of the sample response routes
   *  `next_step` off its LAST step, so position cannot stand in for it. */
  nextStepTitle: string | null;
}
export interface WorkflowVersionStep {
  title: string;
  /** The role's NAME, already resolved — unlike the detail's `role._id`. */
  role: string;
  elements: WorkflowVersionElement[];
}
export interface WorkflowVersionEntry {
  version: number;
  /** Carried for completeness but NOT rendered: every version comes back
   *  published, so it distinguishes nothing — the "Current" pill is decided
   *  by the workflow's own current version instead. */
  versionState: WorkflowVersionState;
  /** ISO — carried through unrenamed. */
  updatedAt: string;
  steps: WorkflowVersionStep[];
}
/** GET /workflows/:id/versions — the workflow's name plus every version of it,
 *  newest first (sorted in the transform, so no component re-sorts). */
export interface WorkflowVersions {
  title: string;
  versions: WorkflowVersionEntry[];
}

// ---- raw shapes (confirmed against the list response) ----
interface ApiWorkflowVersion {
  _id?: string;
  version?: number | string | null;
}
interface ApiWorkflow {
  _id?: string;
  title?: string | null;
  currentVersion?: ApiWorkflowVersion | null;
  /** Id of the published version, or null if never published. */
  publishedVersion?: string | null;
  /** Lifecycle (active/archived) — the tab, not the display status. */
  workflowStatus?: string | null;
  updatedAt?: string | null;
}
type ApiWorkflowPage =
  | { items?: ApiWorkflow[]; totalRecords?: number; total?: number }
  | ApiWorkflow[];

// "vN" label from the current version.
const toVersion = (w: ApiWorkflow): string | null => {
  const raw = w.currentVersion?.version;
  if (raw === null || raw === undefined || raw === '') return null;
  const s = String(raw);
  return /^v/i.test(s) ? s : `v${s}`;
};

// The same figure as the label above, unformatted — the version-history modal
// needs a number to match against the versions payload.
const toVersionNumber = (w: ApiWorkflow): number | null => {
  const raw = w.currentVersion?.version;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(String(raw).replace(/^v/i, ''));
  return Number.isFinite(n) ? n : null;
};

const toState = (w: ApiWorkflow): WorkflowListingState => {
  if (!w.publishedVersion) return WorkflowListingState.Draft; // never published
  const currentId = w.currentVersion?._id ?? null;
  // Published version exists but a newer draft is in progress → both.
  return w.publishedVersion === currentId
    ? WorkflowListingState.Published
    : WorkflowListingState.Both;
};

const toWorkflow = (w: ApiWorkflow): WorkflowItem => ({
  id: w._id ?? '',
  name: w.title ?? '',
  version: toVersion(w),
  versionNumber: toVersionNumber(w),
  state: toState(w),
  updatedAt: w.updatedAt ?? null,
});

// ---- raw selection shape (per the confirmed response) ----
interface ApiWorkflowSelection {
  items: {
    workflowId: string;
    title: string;
    versions: { id: string; version: number }[];
  }[];
}

// ---- raw detail shapes ----
interface ApiDetailElement {
  _id?: string;
  elementGlobalId?: string | null;
  title?: string | null;
  position?: number | null;
  actionType?: string | null;
  nextStep?: string | null;
}
interface ApiDetailStep {
  _id?: string;
  /** Confirmed always sent for a saved step — no fallback downstream. */
  stepGlobalId: string;
  title?: string | null;
  position?: number | null;
  role?: { _id?: string; name?: string } | null;
  elements?: ApiDetailElement[] | null;
}
interface ApiWorkflowDetail {
  _id?: string;
  title?: string | null;
  currentVersion?: {
    _id?: string;
    version?: number | null;
    versionState?: WorkflowVersionState | null;
    steps?: ApiDetailStep[] | null;
  } | null;
}

const byPosition = <T extends { position?: number | null }>(a: T, b: T) =>
  (a.position ?? 0) - (b.position ?? 0);

const toWorkflowDetail = (d: ApiWorkflowDetail): WorkflowDetail => {
  const cv = d.currentVersion;
  return {
    id: d._id ?? null,
    title: d.title ?? '',
    version: cv?.version ?? null,
    versionState: cv?.versionState ?? null,
    steps: [...(cv?.steps ?? [])].sort(byPosition).map((s) => ({
      id: s._id ?? null,
      stepGlobalId: s.stepGlobalId,
      title: s.title ?? '',
      position: s.position ?? 0,
      roleId: s.role?._id ?? '',
      elements: [...(s.elements ?? [])].sort(byPosition).map((e) => ({
        id: e._id ?? '',
        elementGlobalId: e.elementGlobalId ?? null,
        title: e.title ?? '',
        position: e.position ?? 0,
        actionType:
          (e.actionType as WorkflowElementActionType) ?? WorkflowElementActionType.EndWorkflow,
        nextStepId: e.nextStep ?? null,
      })),
    })),
  };
};

export const workflowsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getWorkflows: build.query<WorkflowPage, WorkflowQueryArgs>({
      query: (body) => ({ ...ROUTES.workflows.list, data: body }),
      transformResponse: (res: ApiWorkflowPage) => {
        const items = Array.isArray(res) ? res : (res?.items ?? []);
        const total = Array.isArray(res)
          ? res.length
          : (res?.totalRecords ?? res?.total ?? items.length);
        return { items: items.map(toWorkflow), total };
      },
      providesTags: [{ type: 'Workflow', id: 'LIST' }],
    }),
    // Lightweight workflow + versions list for the form-settings dropdowns.
    getWorkflowsSelection: build.query<WorkflowSelectionItem[], void>({
      query: () => ({ ...ROUTES.workflows.selection }),
      transformResponse: (res: ApiWorkflowSelection) =>
        res.items.map((w) => ({
          id: w.workflowId,
          title: w.title,
          versions: [...w.versions].sort((a, b) => b.version - a.version),
        })),
      providesTags: [{ type: 'Workflow', id: 'SELECTION' }],
    }),
    // Save/publish refresh the list + the selection (new versions must appear
    // in the form-settings dropdowns) — the builder exits to the list right
    // after, so refetching the (still-subscribed) detail would be wasted.
    saveWorkflowDraft: build.mutation<WorkflowSaveResult, WorkflowSavePayload>({
      query: (body) => ({ ...ROUTES.workflows.draft, data: body }),
      invalidatesTags: [
        { type: 'Workflow', id: 'LIST' },
        { type: 'Workflow', id: 'SELECTION' },
      ],
    }),
    publishWorkflow: build.mutation<WorkflowSaveResult, WorkflowSavePayload>({
      query: (body) => ({ ...ROUTES.workflows.publish, data: body }),
      invalidatesTags: [
        { type: 'Workflow', id: 'LIST' },
        { type: 'Workflow', id: 'SELECTION' },
      ],
    }),
    // Row-level lifecycle + duplicate. Both refresh the LIST (a status move
    // carries the row across the Workflows/Archived tabs, a duplicate adds one)
    // and the SELECTION (the form-settings Approval-workflow dropdown reads
    // /workflows/selection) — the pair saveWorkflowDraft and publishWorkflow
    // already invalidate. Typed `unknown`: nothing reads the response, same as
    // formsApi's bulkActionForms.
    setWorkflowStatus: build.mutation<unknown, WorkflowStatusPayload>({
      query: ({ id, status }) => ({ ...ROUTES.workflows.status(id), data: { status } }),
      invalidatesTags: [
        { type: 'Workflow', id: 'LIST' },
        { type: 'Workflow', id: 'SELECTION' },
      ],
    }),
    duplicateWorkflow: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.workflows.duplicate(id) }),
      invalidatesTags: [
        { type: 'Workflow', id: 'LIST' },
        { type: 'Workflow', id: 'SELECTION' },
      ],
    }),
    getWorkflowDetail: build.query<WorkflowDetail, string>({
      query: (id) => ({ ...ROUTES.workflows.detail(id) }),
      transformResponse: (res: ApiWorkflowDetail) => toWorkflowDetail(res),
      // Mutations no longer invalidate per-id detail; drop the cache on unmount
      // so reopening a just-saved workflow always seeds from fresh data.
      keepUnusedDataFor: 0,
    }),
    // Read-only version history for the builder's "Version history" modal. The
    // response needs no mapping — the shape is already the domain shape — so the
    // transform only orders it newest-first (the payload comes back ascending)
    // and the component renders straight from it. keepUnusedDataFor: 0 for the
    // same reason as the detail: publishing mints a new version, so a reopen
    // must never be served a stale list.
    getWorkflowVersions: build.query<WorkflowVersions, string>({
      query: (id) => ({ ...ROUTES.workflows.versions(id) }),
      transformResponse: (res: WorkflowVersions) => ({
        title: res.title,
        versions: [...res.versions].sort((a, b) => b.version - a.version),
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetWorkflowsQuery,
  useGetWorkflowsSelectionQuery,
  useSaveWorkflowDraftMutation,
  usePublishWorkflowMutation,
  useSetWorkflowStatusMutation,
  useDuplicateWorkflowMutation,
  useGetWorkflowDetailQuery,
  useGetWorkflowVersionsQuery,
} = workflowsApi;
